import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TablePagination,
  TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { api } from '../../services/api';
import type { EmballageCustomer, EmballageBranchGroup } from '../../types';
import { useSort, type SortValue } from './sorting';

const BRANCH_GROUPS: Array<{ value: EmballageBranchGroup; label: string }> = [
  { value: 'INDUSTRI', label: 'Industri og fremstilling' },
  { value: 'KONTOR', label: 'Kontor' },
  { value: 'HANDEL', label: 'Handel' },
  { value: 'HOTEL_RESTAURATION_KULTUR', label: 'Hotel, restauration og kultur' },
];

const branchLabel = (v: string | null) => BRANCH_GROUPS.find(b => b.value === v)?.label ?? '';

function customerSortValue(c: EmballageCustomer, key: string): SortValue {
  switch (key) {
    case 'enrolled': return c.enrolled;
    case 'name': return c.customerName;
    case 'cvr': return c.cvr;
    case 'pNumber': return c.pNumber;
    case 'branchGroup': return branchLabel(c.branchGroup);
    default: return c.customerName;
  }
}

interface Props {
  onNotify: (msg: string, error?: boolean) => void;
  onChanged: () => void;
}

export default function EmballageCustomersPanel({ onNotify, onChanged }: Props) {
  const [customers, setCustomers] = useState<EmballageCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyEnrolled, setOnlyEnrolled] = useState(false);
  const [editing, setEditing] = useState<EmballageCustomer | null>(null);
  // Kundelisten kan være meget lang (Viborg har over 11.000). Uden paginering
  // ville tabellen lægge browseren ned.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const load = async () => {
    setLoading(true);
    try {
      setCustomers(await api.getEmballageCustomers());
    } catch {
      onNotify('Kunderne kunne ikke hentes', true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await api.syncEmballageCustomers();
      onNotify(`${r.created} nye kunder, ${r.updated} opdateret`);
      await load();
      onChanged();
    } catch {
      onNotify('Synkroniseringen mod DORA fejlede', true);
    } finally {
      setSyncing(false);
    }
  };

  const toggleEnrolled = async (c: EmballageCustomer) => {
    const next = !c.enrolled;
    setCustomers(prev => prev.map(p => (p.id === c.id ? { ...p, enrolled: next } : p)));
    try {
      await api.updateEmballageCustomer(c.id, { enrolled: next });
      onChanged();
    } catch {
      setCustomers(prev => prev.map(p => (p.id === c.id ? { ...p, enrolled: !next } : p)));
      onNotify('Ændringen blev ikke gemt', true);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return customers.filter(c => {
      if (onlyEnrolled && !c.enrolled) return false;
      if (!q) return true;
      return c.customerName.toLowerCase().includes(q)
        || (c.cvr ?? '').includes(q)
        || c.doraCustomerNumber.includes(q);
    });
  }, [customers, search, onlyEnrolled]);

  const { key, dir, toggle, sorted } = useSort(filtered, customerSortValue, 'name');
  const visible = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Et nyt filter eller en ny sortering skal ikke efterlade brugeren på en
  // side der ikke findes længere.
  useEffect(() => { setPage(0); }, [search, onlyEnrolled, key, dir]);
  const enrolledCount = customers.filter(c => c.enrolled).length;

  const header = (id: string, label: string, align: 'left' | 'center' = 'left') => (
    <TableCell align={align} sortDirection={key === id ? dir : false}>
      <TableSortLabel active={key === id} direction={key === id ? dir : 'asc'} onClick={() => toggle(id)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          size="small"
          placeholder="Søg"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} /> }}
          sx={{ minWidth: 220 }}
        />
        <Button size="small" variant={onlyEnrolled ? 'contained' : 'outlined'} onClick={() => setOnlyEnrolled(v => !v)}>
          Kun tilmeldte ({enrolledCount})
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={handleSync} disabled={syncing}>
          {syncing ? 'Henter' : 'Hent fra DORA'}
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress /></Box>
      ) : customers.length === 0 ? (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">Ingen kunder hentet endnu.</Typography>
        </Card>
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {header('enrolled', 'Med', 'center')}
                {header('name', 'Kunde')}
                {header('cvr', 'CVR')}
                {header('pNumber', 'P-nummer')}
                {header('branchGroup', 'Branchegruppe')}
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map(c => (
                <TableRow key={c.id} hover selected={c.enrolled}>
                  <TableCell align="center" padding="checkbox">
                    <Checkbox size="small" checked={c.enrolled} onChange={() => toggleEnrolled(c)} />
                  </TableCell>
                  <TableCell>{c.customerName}</TableCell>
                  <TableCell>
                    {c.cvr || (c.enrolled
                      ? <Tooltip title="Kunden kan ikke søge uden CVR"><Chip size="small" color="warning" variant="outlined" label="Mangler" /></Tooltip>
                      : '')}
                  </TableCell>
                  <TableCell>{c.pNumber || ''}</TableCell>
                  <TableCell>{branchLabel(c.branchGroup)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setEditing(c)}><EditIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={sorted.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 250]}
            labelRowsPerPage="Pr. side"
            labelDisplayedRows={({ from, to, count }) => `${from} til ${to} af ${count}`}
          />
        </Card>
      )}

      <CustomerDialog
        customer={editing}
        onClose={() => setEditing(null)}
        onSaved={updated => {
          setCustomers(prev => prev.map(p => (p.id === updated.id ? updated : p)));
          setEditing(null);
          onChanged();
        }}
        onNotify={onNotify}
      />
    </Box>
  );
}

function CustomerDialog({ customer, onClose, onSaved, onNotify }: {
  customer: EmballageCustomer | null;
  onClose: () => void;
  onSaved: (c: EmballageCustomer) => void;
  onNotify: (msg: string, error?: boolean) => void;
}) {
  const [form, setForm] = useState<Partial<EmballageCustomer>>({});
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  useEffect(() => { setForm(customer ?? {}); }, [customer]);

  if (!customer) return null;

  const set = (patch: Partial<EmballageCustomer>) => setForm(f => ({ ...f, ...patch }));

  const lookupCvr = async () => {
    const cvr = (form.cvr || '').replace(/\D/g, '');
    if (cvr.length !== 8) {
      onNotify('Indtast et 8-cifret CVR-nummer først', true);
      return;
    }
    setLookingUp(true);
    try {
      const info = await api.lookupCvr(cvr);
      set({
        address: info.address || form.address,
        postcode: info.postalCode || form.postcode,
        city: info.city || form.city,
      });
      onNotify(`Fundet: ${info.name}`);
    } catch {
      onNotify('CVR-nummeret blev ikke fundet', true);
    } finally {
      setLookingUp(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.updateEmballageCustomer(customer.id, {
        cvr: form.cvr?.trim() || null,
        pNumber: form.pNumber?.trim() || null,
        branchGroup: form.branchGroup ?? null,
        address: form.address?.trim() || null,
        postcode: form.postcode?.trim() || null,
        city: form.city?.trim() || null,
        note: form.note?.trim() || null,
      });
      onSaved(updated);
    } catch {
      onNotify('Kunden blev ikke gemt', true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>{customer.customerName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label="CVR" size="small" fullWidth
              value={form.cvr ?? ''} onChange={e => set({ cvr: e.target.value })}
            />
            <Button onClick={lookupCvr} disabled={lookingUp} sx={{ mt: 0.25, whiteSpace: 'nowrap' }}>
              {lookingUp ? 'Slår op' : 'Slå op'}
            </Button>
          </Stack>
          <TextField
            label="P-nummer" size="small" fullWidth
            value={form.pNumber ?? ''} onChange={e => set({ pNumber: e.target.value })}
          />
          <TextField
            select label="Branchegruppe" size="small" fullWidth
            value={form.branchGroup ?? ''}
            onChange={e => set({ branchGroup: (e.target.value || null) as EmballageBranchGroup | null })}
          >
            <MenuItem value="">Ikke valgt</MenuItem>
            {BRANCH_GROUPS.map(b => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
          </TextField>
          <TextField
            label="Adresse" size="small" fullWidth
            value={form.address ?? ''} onChange={e => set({ address: e.target.value })}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Postnummer" size="small" sx={{ width: 140 }}
              value={form.postcode ?? ''} onChange={e => set({ postcode: e.target.value })}
            />
            <TextField
              label="By" size="small" fullWidth
              value={form.city ?? ''} onChange={e => set({ city: e.target.value })}
            />
          </Stack>
          <TextField
            label="Note" size="small" fullWidth multiline rows={2}
            value={form.note ?? ''} onChange={e => set({ note: e.target.value })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annullér</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Gem</Button>
      </DialogActions>
    </Dialog>
  );
}
